/**
 * Single source of truth for the details-step primary CTA.
 *
 * The "what is the payment button and what does it do" decision used to be
 * duplicated across the desktop sidebar, the mobile order-summary bar,
 * BookingForm and PaymentInfo — each re-deriving offerMembership /
 * membershipPlanActive / selectedPaymentMode and rendering its own button. That
 * drift caused the double-submit, missing-continue and hidden-CTA bugs. This
 * hook computes the decision once; render it anywhere with <PaymentCtaButton>.
 *
 * Four outcomes on the details step:
 *   - prepay : DE/NL — the booking is paid up front → validate the form gate,
 *              open Stripe for a one-off invoice. Decided FIRST: a prepay market
 *              has no membership, so none of the membership branches can apply.
 *   - stripe : membership / "pay now" → validate the form gate, open Stripe.
 *   - pick   : membership offered but no option chosen → flag the option cards.
 *   - relay  : everything else → native form submit (the relay request).
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { languageMap } from "@/i18n";
import type { BookingLanguage } from "@/types/booking";
import { useBookingFlow } from "@/hooks/useBookingFlow";
import { useBookingFlowStore } from "@/stores/bookingFlowStore";
import { useMembershipPlanStore } from "@/stores/membershipPlanStore";
import { useEmbeddedPaymentStore } from "@/stores/embeddedPaymentStore";
import { useBloodCheckout } from "@/hooks/useBloodCheckout";
import { useOneOffCheckout } from "@/hooks/useOneOffCheckout";
import { useMarket } from "@/hooks/useMarket";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { marketHasMembership } from "@/config/market";
import { useMembership } from "@/contexts/MembershipContext";
import {
  isMembershipEligible,
  isScanPackage,
  isBundleCart,
} from "@/utils/bloodMembershipPricing";
import { trackMembershipCheckoutInitiated } from "@/utils/analytics";
import { usePrepayEnabled } from "@/hooks/usePrepayEnabled";

export interface PaymentStepCta {
  /** relay → native form submit; prepay/stripe/pick → onClick button. */
  kind: "relay" | "prepay" | "stripe" | "pick";
  label: string;
  disabled: boolean;
  loading: boolean;
  onClick?: () => void;
}

export function usePaymentStepCta(language: BookingLanguage): PaymentStepCta {
  const { t } = useTranslation(undefined, { lng: languageMap[language] });
  const {
    currentStep,
    isSubmitting,
    isSubmitted,
    selectedProductDetails,
    selectedAddOnDetails,
  } = useBookingFlow();

  const { market } = useMarket();
  const prepayEnabled = usePrepayEnabled();
  const { data: flags } = useFeatureFlags();
  const membershipFeatureOn =
    marketHasMembership(market) && (flags?.["membership_v2"] ?? true);

  const selectedPaymentMode = useBookingFlowStore((s) => s.selectedPaymentMode);
  const setPaymentModeError = useBookingFlowStore((s) => s.setPaymentModeError);
  const planActiveRaw = useMembershipPlanStore((s) => s.active);
  const cartHasScan = [...selectedProductDetails, ...selectedAddOnDetails].some(
    isScanPackage,
  );
  // Two shapes pay now via Stripe:
  //   • standalone — a blood-only cart with a membership, billed as the yearly
  //     cadence subscription.
  //   • bundle — a scan + attached membership, charged as one
  //     create-blood-checkout (netted scan one-time items + the cadence's first
  //     invoice). Before bundles existed, a scan + cadence flag could only be an
  //     accidental leak that mispriced the charge, so it was refused (AHD-7853).
  //     Now it's a real product: the expectedOneTimeCents / expectedMembershipCents
  //     409 guards in the edge function keep display == charge, which is the
  //     invariant that guard protected.
  const membershipStandalone =
    membershipFeatureOn && planActiveRaw && !cartHasScan;
  const { isMember } = useMembership();
  // A scan cart is a bundle when membership is chosen via either entry point —
  // the add-ons card (`active`) or path-B "become a member" (cadence yearly).
  const isBundle =
    membershipFeatureOn &&
    isBundleCart({
      cartHasScan,
      membershipActive: planActiveRaw,
      isMember,
      paymentMode: selectedPaymentMode,
    });
  const offerMembership =
    membershipFeatureOn &&
    isMembershipEligible([...selectedProductDetails, ...selectedAddOnDetails]);
  // Path B ("become a member & pay now") is a blood-only, non-member upsell.
  // Excluded for scan carts (which attach a membership only via the bundle) — the
  // same !cartHasScan rule as the "pick" branch below. Without it, a plain MRI
  // booking could reach the Stripe branch and be charged a hidden subscription.
  const isYearlyBlood =
    !isMember &&
    offerMembership &&
    !cartHasScan &&
    selectedPaymentMode === "yearly";

  const { startCheckout, loading: stripeLoading, pricing } = useBloodCheckout();
  const { startCheckout: startOneOffCheckout } = useOneOffCheckout();
  const membershipPlan = useMembershipPlanStore((s) => s.plan);
  const validatePaymentGate = useEmbeddedPaymentStore(
    (s) => s.validatePaymentGate,
  );

  // Click-teleportation guard: when the step changes to "details" the click that
  // advanced the flow can land on the freshly-mounted submit button. Disable it
  // for 100ms so that stray click is swallowed (mirrors the old per-bar logic).
  const prevStepRef = useRef(currentStep);
  const [submitReady, setSubmitReady] = useState(currentStep === "details");
  useEffect(() => {
    if (currentStep === "details" && prevStepRef.current !== "details") {
      setSubmitReady(false);
      const timer = setTimeout(() => setSubmitReady(true), 100);
      prevStepRef.current = currentStep;
      return () => clearTimeout(timer);
    }
    if (currentStep === "details") setSubmitReady(true);
    prevStepRef.current = currentStep;
  }, [currentStep]);

  // Prepay markets (DE/NL) pay the booking up front — decided before every
  // membership branch below, which those markets don't have at all.
  if (prepayEnabled) {
    return {
      kind: "prepay",
      loading: false,
      disabled: !submitReady,
      label: t("orderSummary.continueToPayment"),
      onClick: async () => {
        if (validatePaymentGate && !(await validatePaymentGate())) return;
        startOneOffCheckout();
      },
    };
  }

  // Membership / bundle / pay-now → Stripe (after the form gate passes).
  if (membershipStandalone || isBundle || isYearlyBlood) {
    return {
      kind: "stripe",
      loading: stripeLoading,
      disabled: stripeLoading,
      label: stripeLoading
        ? t("orderSummary.redirecting")
        : t("orderSummary.continueToPayment"),
      onClick: async () => {
        if (validatePaymentGate && !(await validatePaymentGate())) return;
        // A membership checkout is opening — via the cadence flow (path A) or
        // the details-page upsell (path B, AHD-7022). Mark + sync the
        // basket-level membership marker BEFORE the redirect-capable Stripe
        // modal opens (so a TWINT return still classifies the confirmation as
        // a membership purchase), and fire the funnel event. Cadence takes
        // precedence: in the cadence flow selectedPaymentMode sits at its
        // "yearly" default, so isYearlyBlood can be true for path A too.
        const source = membershipStandalone ? "cadence" : "upsell";
        useBookingFlowStore.getState().markMembershipCheckoutInitiated(source);
        trackMembershipCheckoutInitiated(
          source,
          membershipPlan,
          pricing.memberTotalCents,
          language,
          useBookingFlowStore.getState().sessionId,
        );
        startCheckout();
      },
    };
  }

  // Blood-only membership offered but no cadence option picked → flag the once/
  // yearly cards instead of a dead greyed-out button. Scan carts have no such
  // picker (membership is added via the add-ons upsell card and handled by the
  // isBundle branch above), so they never enter here — a plain scan booking with
  // no membership falls through to the normal relay request.
  if (offerMembership && !cartHasScan && selectedPaymentMode === null) {
    return {
      kind: "pick",
      loading: false,
      disabled: false,
      label: t("orderSummary.continueToPayment"),
      onClick: () => {
        setPaymentModeError(true);
        document
          .getElementById("payment-options")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      },
    };
  }

  // Everything else → submit the relay request via the native form submit.
  const submitting = isSubmitting || isSubmitted;
  return {
    kind: "relay",
    loading: submitting,
    disabled: !submitReady || submitting,
    label: submitting
      ? t("orderSummary.submitting")
      : t("orderSummary.submitRequest"),
  };
}
